import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './index.css';

function App() {
  const [formData, setFormData] = useState({
    email: '',
    ideaText: '',
    acceptedTerms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email è obbligatoria';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Inserisci un\'email valida';
    }

    if (!formData.ideaText.trim()) {
      newErrors.ideaText = 'L\'idea è obbligatoria';
    } else if (formData.ideaText.trim().length < 10) {
      newErrors.ideaText = 'L\'idea deve contenere almeno 10 caratteri';
    }

    if (!formData.acceptedTerms) {
      newErrors.acceptedTerms = 'Devi accettare il regolamento';
    }

    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      newErrors.recaptcha = 'Completa la verifica reCAPTCHA';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCancel = () => {
    setFormData({
      email: '',
      ideaText: '',
      acceptedTerms: false
    });
    setErrors({});
    setToast(null);
    recaptchaRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setToast(null);

    // Generate contestSessionId (YYYY-MM-DD)
    const today = new Date();
    const contestSessionId = today.toISOString().split('T')[0];

    // Get reCAPTCHA token
    const recaptchaToken = recaptchaRef.current?.getValue();

    const payload = {
      contestSessionId,
      email: formData.email,
      ideaText: formData.ideaText,
      recaptchaToken
    };

    try {
      const response = await fetch('https://rc9qwy4d45.execute-api.eu-west-1.amazonaws.com/prod/submissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setToast({ 
          type: 'success', 
          title: 'Successo!',
          message: 'La tua idea è stata inviata con successo. Grazie per la partecipazione!' 
        });
        setTimeout(() => {
          setFormData({
            email: '',
            ideaText: '',
            acceptedTerms: false
          });
          setErrors({});
          recaptchaRef.current?.reset();
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setToast({ 
          type: 'danger',
          title: 'Errore',
          message: errorData.message || 'Si è verificato un errore durante l\'invio. Riprova più tardi.' 
        });
      }
    } catch (error) {
      setToast({ 
        type: 'danger',
        title: 'Errore di connessione',
        message: 'Impossibile connettersi al server. Verifica la tua connessione internet e riprova.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${process.env.PUBLIC_URL}/sfondo.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: -1
        }}
      />
      <div className="app-container">
      <div className="form-card">
        <div className="form-header">
          <img 
            src={`${process.env.PUBLIC_URL}/logoTabata.png`} 
            alt="Tabata" 
            className="logo-tabata"
          />
          <p className="mb-0 mt-3">Invia la tua idea innovativa</p>
        </div>
        
        <div className="form-body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="tua@email.com"
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email}</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="ideaText" className="form-label">
                La tua idea <span className="text-danger">*</span>
              </label>
              <textarea
                className={`form-control ${errors.ideaText ? 'is-invalid' : ''}`}
                id="ideaText"
                name="ideaText"
                rows="5"
                value={formData.ideaText}
                onChange={handleChange}
                placeholder="Descrivi la tua idea..."
              />
              {errors.ideaText && (
                <div className="invalid-feedback">{errors.ideaText}</div>
              )}
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className={`form-check-input ${errors.acceptedTerms ? 'is-invalid' : ''}`}
                  id="acceptedTerms"
                  name="acceptedTerms"
                  checked={formData.acceptedTerms}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="acceptedTerms">
                  Ho letto e accetto il{' '}
                  <a 
                    href="https://www.tabatatech.it/sport-booking-la-sfida/?APP" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                  >
                    regolamento del contest
                  </a>
                  <span className="text-danger"> *</span>
                </label>
                {errors.acceptedTerms && (
                  <div className="invalid-feedback d-block">{errors.acceptedTerms}</div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LfyjRAsAAAAAPSwPMyykfKsVcjMIDtX-1-4GDYX"
                onChange={() => {
                  if (errors.recaptcha) {
                    setErrors(prev => ({ ...prev, recaptcha: '' }));
                  }
                }}
              />
              {errors.recaptcha && (
                <div className="text-danger small mt-1">{errors.recaptcha}</div>
              )}
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Annulla
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Invio in corso...
                  </>
                ) : (
                  'Invia'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {toast && (
        <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 9999 }}>
          <div className={`toast show toast-${toast.type}`} role="alert">
            <div className="toast-header">
              <strong className="me-auto">
                {toast.type === 'success' ? '✓ ' : '⚠ '}
                {toast.title}
              </strong>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setToast(null)}
                aria-label="Close"
              ></button>
            </div>
            <div className="toast-body">
              {toast.message}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default App;
